import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Calendar } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import ShareButtons from '../../components/ShareButtons';
import { PUBLIC_BASE } from './config';
import ContentCommentSection from '../../components/public/ContentCommentSection';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const CATEGORY_COLORS = {
  '마케팅 전략': 'bg-blue-500/15 text-blue-400',
  '유통 가이드': 'bg-orange-500/15 text-orange-400',
  '제작 노하우': 'bg-emerald-500/15 text-emerald-400',
  '트렌드 분석': 'bg-amber-500/15 text-amber-400',
  '수익화 전략': 'bg-purple-500/15 text-purple-400',
  '아티스트 성장': 'bg-cyan-500/15 text-cyan-400',
};

function PublicMusicTrendDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`/public/music-trends/${id}`)
      .then((res) => setTrend(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!trend) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-slate-500">
          <p>콘텐츠를 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/magazine/music-trends`)}
            className="text-sky-400 hover:text-sky-300 text-sm">
            목록으로 돌아가기
          </button>
        </div>
      </PublicLayout>
    );
  }

  const colorClass = CATEGORY_COLORS[trend.category] || 'bg-sky-500/15 text-sky-400';
  const dateStr = trend.createdAt
    ? new Date(trend.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <PublicLayout>
      <SEO
        title={trend.title || '뮤직트렌드'}
        description={trend.summary || 'AI가 분석한 음악 트렌드 아티클'}
        path={`/magazine/music-trends/${id}`}
        image={trend.coverImage}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: trend.title,
          description: trend.summary,
          image: trend.coverImage,
          url: `https://impink.co.kr/music-trends/${id}`,
          datePublished: trend.createdAt,
          publisher: { '@type': 'Organization', name: 'p!nk' },
        }}
      />
      {/* 히어로 커버 */}
      <div className="relative w-full" style={{ height: 'clamp(200px, 35vw, 360px)' }}>
        <img
          src={trend.coverImage || FALLBACK_IMG}
          alt={trend.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/60 to-transparent" />

        {/* 뒤로가기 */}
        <button
          onClick={() => navigate(`${PUBLIC_BASE}/magazine/music-trends`)}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full text-white text-sm transition-colors">
          <ArrowLeft size={14} />
          목록
        </button>
      </div>

      {/* 본문 */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-16">
        {/* 메타 */}
        <div className="mb-6">
          <span className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded-full mb-4 ${colorClass}`}>
            {trend.category}
          </span>
          <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight mb-3">
            {trend.title}
          </h1>
          <div className="flex items-center gap-3">
            {dateStr && (
              <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                <Calendar size={12} />
                <span>{dateStr}</span>
              </div>
            )}
            <ShareButtons
              title={trend.title}
              description={trend.summary}
              image={trend.coverImage}
            />
          </div>
        </div>

        {/* 요약 */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 mb-8">
          <p className="text-slate-300 text-sm leading-relaxed">{trend.summary}</p>
        </div>

        {/* HTML 콘텐츠 */}
        <article
          className="music-trend-content"
          dangerouslySetInnerHTML={{ __html: trend.content }}
        />

        <ContentCommentSection targetType="musicTrend" targetId={id} />
      </div>
    </PublicLayout>
  );
}

export default PublicMusicTrendDetailPage;
